import { useState } from 'react';

import { Box, Typography } from '@/shared/ui';

import { useFeedArticles } from '@/entities/feed-article';
import type { MonthlyRecapsArticle } from '@/features/monthly-recaps';

import { mapFeedInfoToArticle } from '@/features/markets/components/MonthlyRecapArticle/mapFeedInfoToArticle';
import { MonthlyRecapArticleModal } from '@/features/markets/components/MonthlyRecapArticle/MonthlyRecapArticleModal';
import { FeedSkeleton } from '../Feed/components/FeedSkeleton';
import { MonthlyRecapsListView } from './MonthlyRecapsListView';
import { MonthlyRecapsSingleView } from './MonthlyRecapsSingleView';

import type { MonthlyRecapsWidgetFilters } from './types';

export type { MonthlyRecapsWidgetFilters } from './types';

const MonthlyRecapsEmptyState = () => (
  <Box
    sx={{
      display: 'flex',
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '160px',
      textAlign: 'center',
      color: 'text.secondary'
    }}
  >
    <Typography>No monthly recap articles available.</Typography>
  </Box>
);

const MonthlyRecapsErrorState = () => (
  <Box
    sx={{
      display: 'flex',
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '160px',
      textAlign: 'center',
      color: 'text.secondary'
    }}
  >
    <Typography>Failed to load monthly recap articles.</Typography>
  </Box>
);

const toFeedKeyCategories = (keyCategory: string | string[] | undefined): string[] => {
  if (Array.isArray(keyCategory)) return keyCategory;
  return keyCategory ? [keyCategory] : [];
};

export const MonthlyRecapsWidget = ({ metadata }: { metadata: MonthlyRecapsWidgetFilters }) => {
  const [openArticle, setOpenArticle] = useState<MonthlyRecapsArticle | null>(null);
  const feedKeyCategories = toFeedKeyCategories(metadata.KeyCategory);
  const { data, isLoading, isSuccess, isError } = useFeedArticles(feedKeyCategories);

  const articles = (data ?? [])
    .filter(item => !metadata.KeyValue || item.symbol.KeyValue === metadata.KeyValue)
    .toSorted(
      (a, b) => new Date(b.transactionTime).getTime() - new Date(a.transactionTime).getTime()
    )
    .map(mapFeedInfoToArticle);

  const hasNoData = isSuccess && articles.length === 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '650px',
        minHeight: '200px',
        overflowY: 'auto'
      }}
    >
      {isLoading && <FeedSkeleton />}
      {hasNoData && <MonthlyRecapsEmptyState />}
      {isError && <MonthlyRecapsErrorState />}

      {!isLoading &&
        !hasNoData &&
        !isError &&
        (metadata.mode === 'single' ? (
          articles[0] && (
            <MonthlyRecapsSingleView
              article={articles[0]}
              onSelectArticle={setOpenArticle}
            />
          )
        ) : (
          <MonthlyRecapsListView
            articles={articles}
            onSelectArticle={setOpenArticle}
          />
        ))}

      {openArticle && (
        <MonthlyRecapArticleModal
          article={openArticle}
          onClose={() => {
            setOpenArticle(null);
          }}
        />
      )}
    </Box>
  );
};
